import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TESTIMONIALS } from "@/lib/content";
import Reveal from "@/components/site/Reveal";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function Testimonials() {
    const [idx, setIdx] = useState(0);
    const t = TESTIMONIALS[idx];

    const next = () => setIdx((i) => (i + 1) % TESTIMONIALS.length);
    const prev = () =>
        setIdx((i) => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);

    return (
        <section
            data-testid="testimonials-section"
            className="bg-[#FAFAFA] text-black px-6 sm:px-10 lg:px-16 py-28 lg:py-40"
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                <div className="lg:col-span-3">
                    <Reveal>
                        <p className="text-[11px] uppercase tracking-[0.3em] text-black/50 mb-6">
                            05 · In their words
                        </p>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="font-serif text-[20vw] lg:text-[14vw] leading-none text-black/10 select-none">
                            &ldquo;
                        </p>
                    </Reveal>
                </div>

                <div className="lg:col-span-9 lg:pl-8">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <p
                                data-testid={`testimonial-quote-${idx}`}
                                className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium leading-[1.15] tracking-tight text-black max-w-4xl"
                            >
                                {t.quote}
                            </p>
                            <div className="mt-10 lg:mt-14 flex items-center gap-4">
                                <div className="w-12 h-px bg-black/40" />
                                <div>
                                    <p className="text-sm font-medium text-black">
                                        {t.name}
                                    </p>
                                    <p className="text-[11px] uppercase tracking-[0.24em] text-black/50 mt-1">
                                        {t.role}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    <div className="mt-14 flex items-center gap-6">
                        <button
                            data-testid="testimonial-prev"
                            onClick={prev}
                            className="w-11 h-11 border border-black/30 flex items-center justify-center hover:bg-black hover:text-white transition-all duration-500"
                            aria-label="Previous"
                        >
                            <ArrowLeft size={14} />
                        </button>
                        <button
                            data-testid="testimonial-next"
                            onClick={next}
                            className="w-11 h-11 border border-black/30 flex items-center justify-center hover:bg-black hover:text-white transition-all duration-500"
                            aria-label="Next"
                        >
                            <ArrowRight size={14} />
                        </button>
                        <div className="ml-4 flex items-center gap-2 font-mono-custom text-xs text-black/50">
                            <span>{String(idx + 1).padStart(2, "0")}</span>
                            <span>/</span>
                            <span>
                                {String(TESTIMONIALS.length).padStart(2, "0")}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
